import { t } from 'i18next'
import { z } from 'zod'

import { validadorEmailDuplicado } from '@/features/proposta/entities/Comprador/validator/email-duplicado-validator'
import { EstadoCivilVinculoValidator } from '@/features/proposta/entities/Comprador/validator/estado-civil-vinculo-validator'
import { tipoDoVinculoDoCompradorEnum } from '@/features/proposta/interfaces/proposta.enums'
import { contatoSchema, estadoCivilSchema, transformEstadoCivil } from '@/features/proposta/schemas/compradores/common'
import { compradorPessoaFisicaBase } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { tipoDeCliente } from '@/shared/interfaces/cliente.enums'
import { telefoneSchema } from '@/shared/schemas'
import { Cnpj } from '@/shared/utils/cnpj'
import { DIGITOS_MAXIMOS_TELEFONE_BRASIL_PJ } from '@/shared/utils/constants'

export type CompradorPessoaJuridicaSchemaInput = z.input<typeof compradorPessoaJuridicaSchema>
export type CompradorPessoaJuridicaSchemaOutput = z.output<typeof compradorPessoaJuridicaSchema>

export const compradorPessoaJuridicaSchema = z
  .object({
    emailsJaVinculados: z.record(z.string(), z.string()),
    tipo: z.literal('PessoaJuridica'),
    responsavel: z
      .object({
        emailsJaVinculados: z.record(z.string(), z.string()),
        fluxoIndependente: z.enum(['Representante']).nullish(),
        tipoDoCliente: z.enum(tipoDeCliente),
        dados: compradorPessoaFisicaBase,
        contato: contatoSchema,
        estadoCivil: estadoCivilSchema
          .superRefine((data, ctx) => {
            const issue = new EstadoCivilVinculoValidator(data).validar()
            if (!issue) return
            ctx.addIssue({
              message: issue.message,
              path: issue.path,
              code: 'custom',
              input: data,
            })
          })
          .transform(transformEstadoCivil),
        tipo: z.enum([tipoDoVinculoDoCompradorEnum.Procurador, tipoDoVinculoDoCompradorEnum.Representante]),
        imagem: z.string().nullish(),
      })
      .nullish(),
    dados: z.object({
      emailsJaVinculados: z.record(z.string(), z.string()),
      cnpj: z
        .string()
        .min(1, { error: 'CNPJ não informado' })
        .refine((cnpj) => new Cnpj(cnpj).validate(), 'CNPJ inválido'),
      razaoSocial: z
        .string()
        .min(1, { error: 'Informe a Razão Social' })
        .max(50, { error: t('proposta:cadastro_compradores.steps.dados_comprador.erro.nome_maximo') }),
      id: z.string().nullish(),
      telefone: telefoneSchema(DIGITOS_MAXIMOS_TELEFONE_BRASIL_PJ),
      buscouCnpj: z.boolean().default(false),
      email: z.email({ error: t('proposta:cadastro_compradores.steps.contatos.erro.email_invalido') }),
    }),
  })
  .extend({ mode: z.enum(['create', 'edit']) })
  .superRefine(({ responsavel, emailsJaVinculados }, ctx) => {
    validadorEmailDuplicado(
      {
        documento: responsavel?.dados.cpf,
        email: responsavel?.contato.email,
        fieldPath: ['responsavel', 'contato', 'email'],
      },
      emailsJaVinculados,
      ctx,
    )
  })
